// src/pages/Campaigns/VolunteerManagement/EventTraining.js
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import DataTableWithFilters from "../../../components/Common/DataTableWithFilters";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  CircularProgress,
  useTheme,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import { format, parseISO } from 'date-fns';

export default function EventTraining() {
  const { chatbotId } = useParams();
  const theme = useTheme();
  
  // State
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  
  // Filter states
  const [trainedFilter, setTrainedFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Training states
  const [isTraining, setIsTraining] = useState(false);
  const [trainingEventId, setTrainingEventId] = useState(null);
  
  // Statistics
  const [statistics, setStatistics] = useState({
    total: 0,
    trained: 0,
    untrained: 0
  });
  
  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null,
    eventId: null
  });
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    event: null
  });

  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axiosInstance.get('/api/events/training/list', {
        params: { chatbot_id: chatbotId }
      });
      
      if (response.data.success) {
        setEvents(response.data.data);
        setStatistics(response.data.statistics);
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      setError(err.response?.data?.message || "Failed to fetch events.");
    } finally {
      setLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Filter logic
  useEffect(() => {
    const filtered = events.filter((event) => {
      // Search filter
      const matchesSearch = Object.values(event)
        .join(" ")
        .toLowerCase()
        .includes(searchText.toLowerCase());

      // Trained filter
      const matchesTrained = trainedFilter === 'all'
        ? true
        : trainedFilter === 'trained'
          ? event.is_trained
          : !event.is_trained;

      // Event type filter
      const matchesEventType = eventTypeFilter
        ? event.event_type === eventTypeFilter
        : true;

      // Status filter
      const matchesStatus = statusFilter
        ? event.status === statusFilter
        : true;

      return matchesSearch && matchesTrained && matchesEventType && matchesStatus;
    });
    setFilteredEvents(filtered);
  }, [searchText, trainedFilter, eventTypeFilter, statusFilter, events]);

  // Train all untrained events
  const handleTrainAll = async () => {
    setIsTraining(true);
    setError("");
    setSuccess("");
    
    try {
      const response = await axiosInstance.post('/api/events/training/train', 
        { retrain_all: false },
        { params: { chatbot_id: chatbotId } }
      );
      
      if (response.data.success) {
        setSuccess(response.data.message);
        fetchEvents();
      }
    } catch (err) {
      console.error("Error training events:", err);
      setError(err.response?.data?.message || "Failed to train events.");
    } finally {
      setIsTraining(false);
    }
  };

  // Train selected events
  const handleTrainSelected = async () => {
    if (selectedEvents.length === 0) {
      setError("No events selected.");
      return;
    }
    
    setIsTraining(true);
    setError("");
    setSuccess("");
    
    try {
      const eventIds = selectedEvents.map(e => e.id);
      const response = await axiosInstance.post('/api/events/training/train',
        { event_ids: eventIds, retrain_all: true },
        { params: { chatbot_id: chatbotId } }
      );
      
      if (response.data.success) {
        setSuccess(response.data.message);
        setSelectedEvents([]);
        setToggleCleared(!toggleCleared);
        fetchEvents();
      }
    } catch (err) {
      console.error("Error training events:", err);
      setError(err.response?.data?.message || "Failed to train events.");
    } finally {
      setIsTraining(false);
    }
  };

  // Retrain single event
  const handleRetrainEvent = async (eventId) => {
    setTrainingEventId(eventId);
    setError("");
    setSuccess("");
    
    try {
      const response = await axiosInstance.post(`/api/events/training/${eventId}`,
        {},
        { params: { chatbot_id: chatbotId } }
      );
      
      if (response.data.success) {
        setSuccess(response.data.message);
        fetchEvents();
      }
    } catch (err) {
      console.error("Error retraining event:", err);
      setError(err.response?.data?.message || "Failed to retrain event.");
    } finally {
      setTrainingEventId(null);
    }
  };

  // Remove event from training
  const handleRemoveFromTraining = async (eventId) => {
    setTrainingEventId(eventId);
    setError("");
    setSuccess("");
    
    try {
      const response = await axiosInstance.delete(`/api/events/training/${eventId}`, {
        params: { chatbot_id: chatbotId }
      });
      
      if (response.data.success) {
        setSuccess(response.data.message);
        fetchEvents();
      }
    } catch (err) {
      console.error("Error removing event from training:", err);
      setError(err.response?.data?.message || "Failed to remove event from training.");
    } finally {
      setTrainingEventId(null);
      setConfirmDialog({ open: false, title: '', message: '', action: null, eventId: null });
    }
  };

  // Confirm dialog handlers
  const openConfirmDialog = (title, message, action, eventId) => {
    setConfirmDialog({ open: true, title, message, action, eventId });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.action === 'remove') {
      handleRemoveFromTraining(confirmDialog.eventId);
    }
  };

  // View training details
  const handleViewDetails = (event) => {
    setDetailDialog({ open: true, event });
  };

  // Table columns
  const columns = [
    {
      name: "Event Name",
      selector: (row) => row.event_name,
      sortable: true,
      grow: 2,
    },
    {
      name: "Event Type",
      selector: (row) => row.event_type,
      sortable: true,
      cell: (row) => (
        <Chip 
          label={row.event_type?.replace('_', ' ') || 'N/A'} 
          size="small" 
          variant="outlined"
        />
      ),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <Chip 
          label={row.status || 'N/A'} 
          size="small"
          color={
            row.status === 'SCHEDULED' ? 'primary' :
            row.status === 'ONGOING' ? 'success' :
            row.status === 'COMPLETED' ? 'default' :
            row.status === 'CANCELLED' ? 'error' : 'default'
          }
        />
      ),
    },
    {
      name: "Event Date",
      selector: (row) => row.event_start_date,
      sortable: true,
      cell: (row) => row.event_start_date 
        ? format(parseISO(row.event_start_date), 'MMM dd, yyyy')
        : 'N/A',
    },
    {
      name: "Trained",
      selector: (row) => row.is_trained,
      sortable: true,
      cell: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          {row.is_trained ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <CancelIcon color="disabled" fontSize="small" />
          )}
          <Typography variant="body2">
            {row.is_trained ? 'Yes' : 'No'}
          </Typography>
        </Box>
      ),
    },
    {
      name: "Trained At",
      selector: (row) => row.trained_at,
      sortable: true,
      cell: (row) => row.trained_at 
        ? format(parseISO(row.trained_at), 'MMM dd, yyyy HH:mm')
        : '-',
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" gap={0.5}>
          {row.is_trained && (
            <Tooltip title="View Training Details">
              <IconButton
                size="small"
                color="info"
                onClick={() => handleViewDetails(row)}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={row.is_trained ? "Retrain Event" : "Train Event"}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleRetrainEvent(row.id)}
              disabled={trainingEventId === row.id}
            >
              {trainingEventId === row.id ? (
                <CircularProgress size={18} />
              ) : (
                <ModelTrainingIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {row.is_trained && (
            <Tooltip title="Remove from Training">
              <IconButton
                size="small"
                color="error"
                onClick={() => openConfirmDialog(
                  'Remove from Training',
                  `Are you sure you want to remove "${row.event_name}" from the training database?`,
                  'remove',
                  row.id
                )}
                disabled={trainingEventId === row.id}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
      width: "150px",
    },
  ];

  // Custom styles
  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"}`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
      },
    },
  };

  // Filter components
  const filterComponents = (
    <>
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Trained</InputLabel>
        <Select
          value={trainedFilter}
          label="Trained"
          onChange={(e) => setTrainedFilter(e.target.value)}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="trained">Trained</MenuItem>
          <MenuItem value="untrained">Not Trained</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Event Type</InputLabel>
        <Select
          value={eventTypeFilter}
          label="Event Type"
          onChange={(e) => setEventTypeFilter(e.target.value)}
        >
          <MenuItem value="">All Types</MenuItem>
          <MenuItem value="RALLY">Rally</MenuItem>
          <MenuItem value="DONATION">Donation</MenuItem>
          <MenuItem value="FUNDRAISER">Fundraiser</MenuItem>
          <MenuItem value="CANVASSING">Canvassing</MenuItem>
          <MenuItem value="PHONE_BANK">Phone Bank</MenuItem>
          <MenuItem value="OTHER">Other</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          label="Status"
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="DRAFT">Draft</MenuItem>
          <MenuItem value="SCHEDULED">Scheduled</MenuItem>
          <MenuItem value="ONGOING">Ongoing</MenuItem>
          <MenuItem value="COMPLETED">Completed</MenuItem>
          <MenuItem value="CANCELLED">Cancelled</MenuItem>
        </Select>
      </FormControl>

      <Button
        variant="outlined"
        startIcon={<RefreshIcon />}
        onClick={fetchEvents}
        disabled={loading}
      >
        Refresh
      </Button>
    </>
  );

  // Selected rows actions
  const selectedRowsActions = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={handleTrainSelected}
        disabled={isTraining}
        startIcon={isTraining ? <CircularProgress size={20} color="inherit" /> : <ModelTrainingIcon />}
      >
        {isTraining ? "Training..." : "Train Selected"}
      </Button>
    </Stack>
  );

  return (
    <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1400px" }}>
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        Event <strong>Training</strong>
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Train your events to make them searchable by the AI chatbot. The chatbot will be able to answer questions about trained events.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="primary">
                {statistics.total}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="success.main">
                {statistics.trained}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Trained Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="warning.main">
                {statistics.untrained}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Untrained Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Train All Button */}
      {statistics.untrained > 0 && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleTrainAll}
            disabled={isTraining}
            startIcon={isTraining ? <CircularProgress size={24} color="inherit" /> : <ModelTrainingIcon />}
          >
            {isTraining ? "Training..." : `Train All Untrained Events (${statistics.untrained})`}
          </Button>
        </Box>
      )}

      {/* Success/Error alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Data Table */}
      <DataTableWithFilters
        columns={columns}
        data={filteredEvents}
        loading={loading}
        filterComponents={filterComponents}
        selectedRowsActions={selectedRowsActions}
        selectedRowsCount={selectedEvents.length}
        onSelectedRowsChange={({ selectedRows }) => setSelectedEvents(selectedRows)}
        clearSelectedRows={toggleCleared}
        noDataMessage="No events found"
        theme={theme}
        customStyles={customDataTableStyles}
        searchText={searchText}
        onSearchChange={(e) => setSearchText(e.target.value)}
        searchPlaceholder="Search events..."
        selectableRows
      />

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Training Details Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, event: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Training Details: {detailDialog.event?.event_name}
        </DialogTitle>
        <DialogContent dividers>
          {detailDialog.event && (
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Summary
              </Typography>
              <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                {detailDialog.event.training_summary || 'No summary available'}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Tags
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {detailDialog.event.training_tags?.split(',').map((tag, index) => (
                  <Chip key={index} label={tag.trim()} size="small" />
                )) || <Typography variant="body2">No tags available</Typography>}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Trained At
              </Typography>
              <Typography variant="body1">
                {detailDialog.event.trained_at 
                  ? format(parseISO(detailDialog.event.trained_at), 'MMMM dd, yyyy HH:mm:ss')
                  : 'N/A'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, event: null })}>
            Close
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              handleRetrainEvent(detailDialog.event.id);
              setDetailDialog({ open: false, event: null });
            }}
            startIcon={<ModelTrainingIcon />}
          >
            Retrain
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}